import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { useToast } from "./toast";
import { play } from "@/lib/sound";

export interface AchievementDef {
    id: string;
    title: string;
    description: string;
    hint: string;
}

export const ACHIEVEMENTS: AchievementDef[] = [
    {
        id: "curious_humans",
        title: "Curious Humans",
        description: "Opened three designated folders.",
        hint: "Three of: my_brain, experiments, outside_the_terminal, unnecessary_thoughts. Opening counts.",
    },
    {
        id: "certified_overthinker",
        title: "Certified Overthinker",
        description: "Explored the 37 tabs, browsing history one by one.",
        hint: "Six different tabs in the simulated browser. Thoughtfully.",
    },
    {
        id: "bug_hunter",
        title: "Bug Hunter",
        description: "Explored the engineering story.",
        hint: "All four stages of The Two-Minute Problem: problem, investigation, solution, lesson.",
    },
    {
        id: "terminally_curious",
        title: "Terminally Curious",
        description: "Executed three valid terminal commands.",
        hint: "terminal.exe knows 'help'. Do you?",
    },
    {
        id: "beyond_keyboard",
        title: "Beyond Keyboard",
        description: "Discovered the saxophone card.",
        hint: "There is a hidden file in outside_the_terminal.",
    },
    {
        id: "chaos_engineer",
        title: "Chaos Engineer",
        description: "Activated a combination mode.",
        hint: "Push two personality sliders into a conflict.",
    },
    {
        id: "no_stone_unturned",
        title: "No Stone Unturned",
        description: "Discovered every designated easter egg.",
        hint: "Eleven eggs, hidden across the OS: the boot screen, the desktop wallpaper, the taskbar (clock and logo), folders, the recycle bin, the terminal, the browser — and one secret word typed anywhere.",
    },
];

export const EGGS = [
    "egg_boot_404",
    "egg_doodle_star",
    "egg_sax_file",
    "egg_recycle_secret",
    "egg_terminal_coffee",
    "egg_hidden_tab",
    "egg_clock_watcher",
    "egg_paper_trail",
    "egg_logo_poker",
    "egg_wallpaper_cleaner",
    "egg_secret_knock",
] as const;

export const FOLDER_IDS = ["my_brain", "experiments", "outside_the_terminal", "unnecessary_thoughts"];

interface Persist {
    unlocked: string[];
    folders: string[];
    tabs: string[];
    stages: number[];
    terminalCount: number;
    eggs: string[];
}

const EMPTY: Persist = { unlocked: [], folders: [], tabs: [], stages: [], terminalCount: 0, eggs: [] };

function load(): Persist {
    try {
        const raw = localStorage.getItem("krithios:achievements");
        if (raw) return { ...EMPTY, ...(JSON.parse(raw) as Persist) };
    } catch {
        /* fresh start */
    }
    return EMPTY;
}

function persist(p: Persist) {
    try {
        localStorage.setItem("krithios:achievements", JSON.stringify(p));
    } catch {
        /* in-memory only */
    }
}

interface AchievementsApi {
    unlocked: Record<string, boolean>;
    unlockedCount: number;
    eggs: string[];
    allEggs: boolean;
    unlock: (id: string) => void;
    trackFolder: (id: string) => void;
    trackTab: (id: string) => void;
    trackStage: (i: number) => void;
    trackTerminal: () => void;
    trackEgg: (id: string) => void;
}

const Ctx = createContext<AchievementsApi | null>(null);

export function AchievementsProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<Persist>(load);
    const stateRef = useRef(state);
    stateRef.current = state;
    const { push } = useToast();

    const commit = useCallback((next: Persist) => {
        stateRef.current = next;
        setState(next);
        persist(next);
    }, []);

    const unlock = useCallback(
        (id: string) => {
            const def = ACHIEVEMENTS.find((a) => a.id === id);
            if (!def || stateRef.current.unlocked.includes(id)) return;
            commit({ ...stateRef.current, unlocked: [...stateRef.current.unlocked, id] });
            play("success");
            push("achievement", `Achievement unlocked — ${def.title}`, def.description);
        },
        [commit, push],
    );

    const trackFolder = useCallback(
        (id: string) => {
            const p = stateRef.current;
            if (p.folders.includes(id) || !FOLDER_IDS.includes(id)) return;
            const next = { ...p, folders: [...p.folders, id] };
            commit(next);
            if (next.folders.length >= 3) unlock("curious_humans");
        },
        [commit, unlock],
    );

    const trackTab = useCallback(
        (id: string) => {
            const p = stateRef.current;
            if (p.tabs.includes(id)) return;
            const next = { ...p, tabs: [...p.tabs, id] };
            commit(next);
            if (next.tabs.length >= 6) unlock("certified_overthinker");
        },
        [commit, unlock],
    );

    const trackStage = useCallback(
        (i: number) => {
            const p = stateRef.current;
            if (p.stages.includes(i)) return;
            const next = { ...p, stages: [...p.stages, i] };
            commit(next);
            if (next.stages.length >= 4) unlock("bug_hunter");
        },
        [commit, unlock],
    );

    const trackTerminal = useCallback(() => {
        const p = stateRef.current;
        const next = { ...p, terminalCount: p.terminalCount + 1 };
        commit(next);
        if (next.terminalCount >= 3) unlock("terminally_curious");
    }, [commit, unlock]);

    const trackEgg = useCallback(
        (id: string) => {
            const p = stateRef.current;
            if (p.eggs.includes(id) || !(EGGS as readonly string[]).includes(id)) return;
            const next = { ...p, eggs: [...p.eggs, id] };
            commit(next);
            play("egg");
            push("egg", `Easter egg found (${next.eggs.length}/${EGGS.length})`, "somewhere, a checkbox ticks. keep digging.");
            if (EGGS.every((e) => next.eggs.includes(e))) unlock("no_stone_unturned");
        },
        [commit, push, unlock],
    );

    const api = useMemo<AchievementsApi>(() => {
        const unlocked: Record<string, boolean> = {};
        state.unlocked.forEach((id) => (unlocked[id] = true));
        return {
            unlocked,
            unlockedCount: state.unlocked.length,
            eggs: state.eggs,
            allEggs: EGGS.every((e) => state.eggs.includes(e)),
            unlock,
            trackFolder,
            trackTab,
            trackStage,
            trackTerminal,
            trackEgg,
        };
    }, [state, unlock, trackFolder, trackTab, trackStage, trackTerminal, trackEgg]);

    return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useAchievements(): AchievementsApi {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error("useAchievements must be used inside AchievementsProvider");
    return ctx;
}
